import { useEffect, useState } from "react";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { getReportHistory, getReportDownloadUrl } from "../../../service/cat15.service";
import { ReportFormat, ReportHistoryItem } from "../../../models/dto/cat15/cat15.types";
import { downloadFromUrl } from "../../../service/report.service";
import { formatDate } from "../format";

const HistoryTab = () => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [rows, setRows] = useState<ReportHistoryItem[]>([]);

  useEffect(() => {
    getReportHistory()
      .then(setRows)
      .catch(() => undefined); // handleApiError already surfaced the alert
  }, []);

  // Fetch a fresh presigned URL for the chosen format, then trigger the download.
  const handleDownload = async (row: ReportHistoryItem, format: ReportFormat) => {
    const link = await getReportDownloadUrl(row.id, format).catch(() => null);
    if (link) downloadFromUrl(link.url, link.fileName);
  };

  // excel first, then pdf — one download button per available format (like the TGO report table).
  const orderedFormats = (row: ReportHistoryItem): ReportFormat[] =>
    [...(row.formats.length ? row.formats : (["excel"] as ReportFormat[]))].sort((a, b) =>
      a === "excel" ? -1 : b === "excel" ? 1 : 0,
    );

  const columns: GridColDef<ReportHistoryItem>[] = [
    {
      field: "generatedOn",
      headerName: t("Col-Generated-On"),
      flex: 1.2,
      minWidth: 160,
      valueFormatter: (value) => formatDate(value as string),
    },
    { field: "year", headerName: t("Filter-Reporting-Year"), width: 120 },
    { field: "format", headerName: t("Col-Format"), flex: 1, minWidth: 130 },
    { field: "by", headerName: t("Col-Generated-By"), flex: 1, minWidth: 130 },
    {
      field: "status",
      headerName: t("Col-Status"),
      width: 120,
      renderCell: (params) => {
        const ready = params.row.status === "ready";
        return (
          <Chip
            size="small"
            label={ready ? t("Status-Ready") : t("Status-Failed")}
            sx={{
              borderRadius: "6px",
              fontWeight: 600,
              color: ready
                ? "var(--status-approved-complete-active-text)"
                : "var(--status-rejected-failed-text)",
              backgroundColor: ready
                ? "var(--status-approved-complete-active-bg)"
                : "var(--status-rejected-failed-bg)",
            }}
          />
        );
      },
    },
    {
      field: "download",
      headerName: t("Col-Download"),
      width: 120,
      sortable: false,
      renderCell: (params) =>
        params.row.status === "ready" ? (
          <Stack direction="row" gap="6px" alignItems="center" height="100%">
            {orderedFormats(params.row).map((format) => (
              <Tooltip key={format} title={format.toUpperCase()}>
                <IconButton
                  size="small"
                  sx={{
                    color: "var(--white)",
                    backgroundColor: format === "pdf" ? "var(--red)" : "var(--green)",
                    "&:hover": { backgroundColor: format === "pdf" ? "var(--red)" : "var(--green)", opacity: 0.85 },
                  }}
                  onClick={() => handleDownload(params.row, format)}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
          </Stack>
        ) : null,
    },
  ];

  return (
    <Box
      bgcolor="var(--white)"
      border="1px solid var(--gray5)"
      borderRadius="8px"
    >
      <AppDataGrid
        rows={rows}
        columns={columns}
        autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10, 25]}
      />
    </Box>
  );
};

export default HistoryTab;
